import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AspirantSignupResponse } from '@udbodhanam/shared';
import apiClient from '../../services/apiClient';
import { loadRazorpayCheckout, PAYMENT_RECOVERY_KEY, RAZORPAY_DISMISS_POLL_MS, RAZORPAY_POLL_INTERVAL_MS, RAZORPAY_POLL_TIMEOUT_MS, RAZORPAY_TIMEOUT_POLL_INTERVAL_MS } from '../../lib/razorpay';
import { useAuthStore } from '../../stores/useAuthStore';
import { useLanguageStore } from '../../stores/useLanguageStore';
import i18n from '../../i18n/config';

interface MeResponse {
  data: {
    id: string;
    username: string;
    fullName: string;
    role: string;
    languagePreference: string;
    consentStatus: string;
    mustChangePassword: boolean;
    accountType: string;
    paymentAccessReason: 'NEVER_PAID' | 'EXPIRED' | null;
  };
}

type CheckoutState =
  | { phase: 'opening' }
  | { phase: 'polling' }
  | { phase: 'dismissed' }
  | { phase: 'success' }
  | { phase: 'failed' }
  | { phase: 'timeout' }
  | { phase: 'retrying'; data: AspirantSignupResponse };

interface StatusResponse {
  status: string;
  exchangeToken?: string;
}

const ROLE_HOME: Record<string, string> = {
  ADMIN: '/admin',
  MENTOR: '/mentor',
  STUDENT: '/student',
};

interface Props {
  checkoutData: AspirantSignupResponse;
  skipModal?: boolean;
  // 'signup' (default) — the aspirant has no session yet; on success we
  // exchange the one-time token and auto-login (existing behaviour, unchanged
  // call site in AspirantSignup.tsx needs no update).
  // 'renewal' (Story 10.6) — the student is ALREADY authenticated; skip the
  // exchange-token/auto-login branch entirely and just refresh the session
  // with the newly-extended access_expires_at, then go to their dashboard.
  mode?: 'signup' | 'renewal';
  // When true (NEVER_PAID path from PaymentRequired), trigger the onboarding
  // tour after renewal succeeds — the user never completed signup-flow payment
  // so they haven't seen the tour yet.
  showTourOnSuccess?: boolean;
}

export default function RazorpayCheckout({ checkoutData: initialData, skipModal = false, mode = 'signup', showTourOnSuccess = false }: Props) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [state, setState] = useState<CheckoutState>(
    skipModal ? { phase: 'polling' } : { phase: 'opening' }
  );
  // Persists across phase transitions — updated when a reorder creates a new order.
  // Cannot be derived from state.phase because it must survive retrying → polling.
  const [currentData, setCurrentData] = useState<AspirantSignupResponse>(initialData);
  // When recovering from a refresh, start the 90s timeout clock fresh.
  const pollingStartedAt = useRef<number>(skipModal ? Date.now() : 0);
  const dismissedAt = useRef<number>(0);
  const hasNavigated = useRef(false);
  const handlerFired = useRef(false);

  // Write recovery entry only after payment has been initiated.
  // polling = handler fired (payment submitted); dismissed = UPI modal auto-closed.
  // Opening/retrying are excluded so back-button-without-paying never triggers recovery.
  // failed = clear recovery so a back-button-without-paying user can sign up fresh.
  useEffect(() => {
    if (state.phase === 'polling' || state.phase === 'dismissed') {
      sessionStorage.setItem(PAYMENT_RECOVERY_KEY, JSON.stringify(currentData));
    } else if (state.phase === 'failed') {
      sessionStorage.removeItem(PAYMENT_RECOVERY_KEY);
    }
  }, [state.phase, currentData]);

  // ── Open Razorpay modal ──────────────────────────────────────────────────
  const openModal = (data: AspirantSignupResponse) => {
    handlerFired.current = false; // reset for each new modal
    loadRazorpayCheckout()
      .then(() => {
        const rzp = new window.Razorpay!({
          key: data.razorpay_key_id,
          order_id: data.razorpay_order_id,
          name: 'Udbodhanam',
          description: t('payment.description'),
          handler: () => {
            // Defense-in-depth only — never grant access here. Transition to
            // polling faster, but the webhook is the sole source of truth.
            handlerFired.current = true;
            pollingStartedAt.current = Date.now();
            setState({ phase: 'polling' });
          },
          modal: {
            ondismiss: () => {
              if (!handlerFired.current) {
                // User dismissed without paying. Start a short 20s window to
                // catch UPI payments that auto-close the modal before completing.
                dismissedAt.current = Date.now();
                setState({ phase: 'dismissed' });
              } else {
                // Modal closed after handler fired — keep polling.
                pollingStartedAt.current = Date.now();
                setState({ phase: 'polling' });
              }
            },
          },
        });
        rzp.open();
      })
      .catch(() => setState({ phase: 'failed' }));
  };

  // Open on mount (and on retry when phase becomes 'retrying')
  useEffect(() => {
    if (state.phase === 'opening') {
      openModal(currentData);
    }
    if (state.phase === 'retrying') {
      openModal(currentData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // ── Status polling ───────────────────────────────────────────────────────
  // timeout keeps polling at a reduced rate so a delayed webhook can still
  // auto-recover the UI without the user needing to manually log in.
  const isPolling = state.phase === 'polling' || state.phase === 'dismissed' || state.phase === 'timeout';

  const { data: statusData } = useQuery<StatusResponse>({
    queryKey: ['payment-status', currentData.payments_id],
    queryFn: () =>
      apiClient
        .get<{ data: StatusResponse }>(`/aspirant/payments/${currentData.payments_id}/status`)
        .then((r) => r.data.data),
    enabled: isPolling,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'paid' || status === 'failed') return false;
      if (state.phase === 'polling' && Date.now() - pollingStartedAt.current > RAZORPAY_POLL_TIMEOUT_MS) return false;
      if (state.phase === 'dismissed' && Date.now() - dismissedAt.current > RAZORPAY_DISMISS_POLL_MS) return false;
      if (state.phase === 'timeout') return RAZORPAY_TIMEOUT_POLL_INTERVAL_MS;
      return RAZORPAY_POLL_INTERVAL_MS;
    },
  });

  // React to polling results
  useEffect(() => {
    if (!isPolling || !statusData) return;

    // Signup: wait for the exchange token (no session yet, so it's required
    // to log in). Renewal: the student is already logged in — 'paid' alone
    // is enough, the exchange token (if the endpoint happens to issue one
    // anyway) is simply unused.
    const isPaid =
      mode === 'renewal' ? statusData.status === 'paid' : statusData.status === 'paid' && !!statusData.exchangeToken;

    if (isPaid && !hasNavigated.current) {
      setState({ phase: 'success' });
      hasNavigated.current = true;

      if (mode === 'renewal') {
        apiClient
          .get<MeResponse>('/users/me')
          .then((meRes) => {
            const u = meRes.data.data;
            setAuth(u, u.role, u.consentStatus, u.mustChangePassword ?? false, false);
            if (showTourOnSuccess) sessionStorage.setItem('aspirantFirstLogin', 'true');
            navigate(ROLE_HOME[u.role] ?? '/student', { replace: true });
          })
          .catch(() => navigate('/student', { replace: true }));
        return;
      }

      apiClient
        .post<{ data: { isFirstLogin: boolean } }>('/auth/razorpay-exchange', { exchangeToken: statusData.exchangeToken })
        .then((exchangeRes) => {
          const isFirstLogin = exchangeRes.data.data.isFirstLogin;
          return apiClient.get<MeResponse>('/users/me').then((meRes) => ({ meRes, isFirstLogin }));
        })
        .then(({ meRes, isFirstLogin }) => {
          const u = meRes.data.data;
          setAuth(u, u.role, u.consentStatus, u.mustChangePassword ?? false, isFirstLogin);
          const lang = (u.languagePreference ?? 'EN').toLowerCase();
          sessionStorage.setItem('appLang', lang);
          useLanguageStore.getState().setLanguage(lang);
          return i18n.changeLanguage(lang);
        })
        .then(() => {
          sessionStorage.removeItem(PAYMENT_RECOVERY_KEY);
          sessionStorage.setItem('aspirantFirstLogin', 'true');
          navigate('/student', { replace: true });
        })
        .catch(() => {
          sessionStorage.removeItem(PAYMENT_RECOVERY_KEY);
          navigate('/login', { replace: true });
        });
      return;
    }

    if (statusData.status === 'failed') {
      setState({ phase: 'failed' });
      return;
    }

    if (state.phase === 'polling' && Date.now() - pollingStartedAt.current > RAZORPAY_POLL_TIMEOUT_MS) {
      setState({ phase: 'timeout' });
      return;
    }

    if (state.phase === 'dismissed' && Date.now() - dismissedAt.current > RAZORPAY_DISMISS_POLL_MS) {
      setState({ phase: 'failed' });
    }
  }, [statusData, state.phase, isPolling, navigate, setAuth, mode, showTourOnSuccess]);

  // Guarantee polling → timeout and dismissed → failed after their respective
  // deadlines, regardless of whether polling is still running. The statusData
  // effect alone is unreliable — it only fires when new data arrives, so if
  // polling stalls or stops just before the threshold the transition never fires.
  useEffect(() => {
    if (state.phase === 'polling') {
      const timer = setTimeout(() => {
        setState((prev) => (prev.phase === 'polling' ? { phase: 'timeout' } : prev));
      }, RAZORPAY_POLL_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
    if (state.phase === 'dismissed') {
      const timer = setTimeout(() => {
        setState((prev) => (prev.phase === 'dismissed' ? { phase: 'failed' } : prev));
      }, RAZORPAY_DISMISS_POLL_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [state.phase]);

  // ── Retry handler ────────────────────────────────────────────────────────
  const handleRetry = async () => {
    try {
      const res = await apiClient.post<{ data: AspirantSignupResponse }>('/aspirant/payments/reorder', {
        user_id: currentData.user_id,
      });
      sessionStorage.setItem(PAYMENT_RECOVERY_KEY, JSON.stringify(res.data.data));
      setCurrentData(res.data.data);
      setState({ phase: 'retrying', data: res.data.data });
    } catch {
      // Stay on failed state; user can try again
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <main className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="bg-surface border border-border rounded-xl shadow-card p-8 max-w-md w-full text-center space-y-4">

        {(state.phase === 'opening' || state.phase === 'retrying') && (
          <>
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
            <p className="text-sm text-earth-600">{t('payment.openingMessage')}</p>
          </>
        )}

        {state.phase === 'polling' && (
          <>
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-earth-800">{t('payment.verifyingTitle')}</h2>
            <p className="text-sm text-earth-600">{t('payment.verifyingMessage')}</p>
          </>
        )}

        {state.phase === 'dismissed' && (
          <>
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-earth-800">{t('payment.dismissedTitle')}</h2>
            <p className="text-sm text-earth-600">{t('payment.dismissedMessage')}</p>
          </>
        )}

        {state.phase === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-earth-800">{t('payment.successTitle')}</h2>
            <p className="text-sm text-earth-600">{t('payment.successMessage')}</p>
          </>
        )}

        {state.phase === 'failed' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-earth-800">{t('payment.failedTitle')}</h2>
            <p className="text-sm text-earth-600">{t('payment.failedMessage')}</p>
            <button
              type="button"
              onClick={() => void handleRetry()}
              className="w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600"
            >
              {t('payment.retryButton')}
            </button>
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(PAYMENT_RECOVERY_KEY);
                navigate('/login', { replace: true });
              }}
              className="w-full border border-border text-earth-700 font-medium rounded-lg py-2.5 text-sm hover:bg-surface-muted"
            >
              {t('payment.backToLogin')}
            </button>
          </>
        )}

        {state.phase === 'timeout' && (
          <>
            <div className="w-12 h-12 rounded-full bg-viveka-100 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-viveka-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-earth-800">{t('payment.timeoutTitle')}</h2>
            <p className="text-sm text-earth-600">{t('payment.timeoutMessage')}</p>
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(PAYMENT_RECOVERY_KEY);
                navigate('/login', { replace: true });
              }}
              className="w-full border border-border text-earth-700 font-medium rounded-lg py-2.5 text-sm hover:bg-surface-muted"
            >
              {t('payment.tryLoginButton')}
            </button>
          </>
        )}

      </div>
    </main>
  );
}
